import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building2, Search } from "lucide-react";
import { api, apiError, cn, fmtDate, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, Chip, Empty, Input, Skeleton } from "../components/ui";
import ScoreListCard from "../components/ScoreListCard";

/** Superadmin: every club on the platform. */
export function AdminClubsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.is_superadmin) return;
    let alive = true;
    const t = setTimeout(() => {
      api.get("/admin/clubs", { params: { search } })
        .then(({ data }) => alive && setData(data))
        .catch((err) => toast.error(apiError(err)));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [search, user]);

  if (!user?.is_superadmin) return <Navigate to="/home" replace />;

  return (
    <div className="pb-24">
      <Header title="All clubs" subtitle={data ? `${data.total} club${data.total === 1 ? "" : "s"}` : "Superadmin"} onBack={() => nav("/admin")} />
      <div className="px-5">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <Input placeholder="Search by name, city or join code" value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="admin-club-search" />
        </div>
      </div>
      <div className="px-5 mt-4 space-y-3">
        {!data ? (
          <><Skeleton className="h-28" /><Skeleton className="h-28" /></>
        ) : data.clubs.length === 0 ? (
          <Card className="p-0"><Empty icon={Building2} title="No clubs found" /></Card>
        ) : data.clubs.map((c) => (
          <Card key={c.id} className="py-4 px-5 cursor-pointer" onClick={() => nav(`/admin/clubs/${c.id}`)} data-testid={`admin-club-${c.id}`}>
            <div className="flex items-start gap-3">
              <ClubLogo club={c} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold truncate">{c.name}</span>
                  {c.archived && <Chip tone="danger">Archived</Chip>}
                </div>
                <div className="text-xs text-ink-muted truncate">
                  {[c.city, c.country].filter(Boolean).join(", ") || "—"} • Code {c.join_code}
                </div>
                <div className="text-xs text-ink-muted truncate mt-0.5">
                  Owner: {c.owner.name || "?"}{c.owner.email ? ` (${c.owner.email})` : ""}
                </div>
              </div>
              <PlanChip plan={c.plan} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-black/[0.06] text-center">
              <Mini value={c.members} label="Members" />
              <Mini value={c.matches} label="Matches" />
              <Mini value={c.last_match_at ? fmtDate(c.last_match_at) : "—"} label="Last match" small />
            </div>
            <div className="text-[11px] text-ink-muted mt-2">Created {fmtDate(c.created_at)}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/** Superadmin: one club's members, money and recent matches. */
export function AdminClubDetailPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.is_superadmin) return;
    api.get(`/admin/clubs/${id}`)
      .then(({ data }) => setData(data))
      .catch((err) => { toast.error(apiError(err)); nav("/admin/clubs", { replace: true }); });
  }, [id, user, nav]);

  if (!user?.is_superadmin) return <Navigate to="/home" replace />;
  if (!data) {
    return (
      <div className="pb-24">
        <Header title="Club" onBack={() => nav("/admin/clubs")} />
        <div className="px-5 space-y-3"><Skeleton className="h-32" /><Skeleton className="h-60" /></div>
      </div>
    );
  }

  const { club, owner, plan, members, counts } = data;
  const players = members.filter((m) => !m.guest);
  const guests = members.length - players.length;

  return (
    <div className="pb-24">
      <Header title={club.name} subtitle={[club.city, club.country].filter(Boolean).join(", ") || "Club"} onBack={() => nav("/admin/clubs")} />

      <div className="px-5 space-y-4">
        <Card>
          <div className="flex items-start gap-3">
            <ClubLogo club={club} />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-semibold">{owner?.name || "?"}</div>
              <div className="text-ink-muted truncate">{owner?.email}</div>
              <div className="text-xs text-ink-muted mt-1">
                Code {club.join_code} • Created {fmtDate(club.created_at)}
                {club.archived && " • Archived"}
              </div>
            </div>
            <PlanChip plan={plan} />
          </div>
          <div className="grid grid-cols-4 gap-2 mt-4 pt-3 border-t border-black/[0.06] text-center">
            <Mini value={players.length} label="Members" />
            <Mini value={counts.matches} label="Matches" />
            <Mini value={counts.polls} label="Polls" />
            <Mini value={inr(data.total_pending)} label="Pending" small />
          </div>
        </Card>

        <div>
          <SectionTitle>Members{guests ? ` • ${guests} guest${guests === 1 ? "" : "s"}` : ""}</SectionTitle>
          <Card className="p-2 divide-y divide-black/[0.06]">
            {players.length === 0 ? (
              <div className="text-sm text-ink-muted p-3">No members yet</div>
            ) : players.map((m) => (
              <div key={m.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className={cn("text-sm font-semibold truncate", !m.active && "text-ink-muted line-through")}>{m.name}</div>
                  <div className="text-xs text-ink-muted truncate">{m.email || "No app account"}</div>
                </div>
                {m.club_role && m.club_role !== "member" && <Chip className="capitalize">{m.club_role}</Chip>}
                <div className={cn("text-sm font-bold tabular-nums", m.balance < 0 ? "text-red-500" : "text-ink")}>{inr(m.balance)}</div>
              </div>
            ))}
          </Card>
        </div>

        <div>
          <SectionTitle>Recent matches</SectionTitle>
          {data.recent_matches.length === 0 ? (
            <Card className="text-sm text-ink-muted">No matches scored yet</Card>
          ) : (
            <div className="space-y-3">
              {data.recent_matches.map((s) => <ScoreListCard key={s.id} s={s} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Header({ title, subtitle, onBack }) {
  return (
    <div className="px-5 pt-6 pb-4 flex items-center gap-3">
      <button onClick={onBack} className="h-10 w-10 rounded-2xl bg-black/5 grid place-items-center btn-press" aria-label="Back">
        <ArrowLeft size={18} />
      </button>
      <div className="min-w-0">
        {subtitle && <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold truncate">{subtitle}</div>}
        <h1 className="text-2xl font-bold text-ink leading-tight truncate">{title}</h1>
      </div>
    </div>
  );
}

function ClubLogo({ club }) {
  return club.logo
    ? <img src={club.logo} alt="" className="h-11 w-11 rounded-2xl object-cover shrink-0" />
    : <div className="h-11 w-11 rounded-2xl bg-brand-50 text-brand grid place-items-center font-bold shrink-0">{club.name?.[0]?.toUpperCase()}</div>;
}

function PlanChip({ plan }) {
  const tone = plan.plan_id === "premier" ? "warning" : plan.plan_id === "league" ? "default" : "success";
  return <Chip tone={tone} className="shrink-0">{plan.plan_name}</Chip>;
}

function Mini({ value, label, small }) {
  return (
    <div className="min-w-0">
      <div className={cn("font-extrabold tabular-nums truncate", small ? "text-sm leading-7" : "text-lg")}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">{label}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-3">{children}</div>;
}
